import { Injectable } from "@angular/core";
import * as papa from "papaparse";

import { extractFileType } from "@bitwarden/web-vault/app/shared/utils/helper-string";
import {
  isSpreadsheetFileName,
  spreadsheetBufferToCsv,
  spreadsheetBufferToSheets,
} from "@bitwarden/web-vault/app/shared/utils/file-upload/spreadsheet.util";

/** A spreadsheet-style preview: the header row plus the remaining data rows. */
export type FilePreviewGrid = {
  headers: string[];
  rows: string[][];
};

/** One worksheet (tab) parsed into a preview grid. */
export type FileSheetGrid = {
  name: string;
  grid: FilePreviewGrid;
};

/**
 * Reusable upload helper for tabular files (csv / txt / xls / xlsx).
 * Normalizes any supported file into CSV text and a spreadsheet-style grid so
 * the same flow can power the reference-data importer, the cabinet upload, and
 * the file-preview component.
 */
@Injectable({
  providedIn: "root",
})
export class FileUploadService {
  /** Extensions that can be parsed into a tabular preview grid. */
  private readonly tabularExtensions = ["csv", "txt", "xls", "xlsx"];

  private getExtension(file: File): string {
    return (extractFileType(file) ?? "").toLowerCase();
  }

  /** True for Excel workbooks (.xlsx / .xls) that need binary parsing. */
  isSpreadsheet(file: File): boolean {
    return isSpreadsheetFileName(file?.name);
  }

  /** True for any file we can render as a spreadsheet-style table. */
  isTabular(file: File): boolean {
    return this.tabularExtensions.includes(this.getExtension(file));
  }

  /**
   * Normalize any supported tabular file into CSV text.
   * Excel workbooks are converted via SheetJS; csv/txt are read as UTF-8.
   */
  async getCsvContent(file: File): Promise<string> {
    if (this.isSpreadsheet(file)) {
      const buffer = await this.readAsArrayBuffer(file);
      return spreadsheetBufferToCsv(buffer);
    }
    return this.readAsText(file);
  }

  /**
   * Read a tabular file into one preview grid per worksheet (tab), Excel-style.
   * Excel workbooks return every sheet; csv/txt return a single sheet named after the file.
   */
  async getSheetGrids(file: File): Promise<FileSheetGrid[]> {
    if (this.isSpreadsheet(file)) {
      const buffer = await this.readAsArrayBuffer(file);
      const sheets = spreadsheetBufferToSheets(buffer);
      return sheets.map((sheet) => ({
        name: sheet.name,
        grid: this.parseCsvToGrid(sheet.csv),
      }));
    }
    const content = await this.readAsText(file);
    return [{ name: file?.name ?? "Sheet1", grid: this.parseCsvToGrid(content) }];
  }

  /** Parse raw CSV text into a header row + non-empty data rows. */
  parseCsvToGrid(content: string): FilePreviewGrid {
    const parsed = (
      (papa.parse<string[]>(content, { skipEmptyLines: false }).data ?? []) as any[]
    ).filter(
      (row) => Array.isArray(row) && row.some((cell) => cell != null && String(cell).trim() !== ""),
    );
    return {
      headers: parsed.length ? parsed[0] : [],
      rows: parsed.slice(1),
    };
  }

  private readAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(file, "utf-8");
      reader.onload = (evt) => resolve((evt.target as any).result);
      reader.onerror = () => reject(reader.error);
    });
  }

  private readAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = (evt) => resolve((evt.target as any).result);
      reader.onerror = () => reject(reader.error);
    });
  }
}
